import {
  InfiniteTable,
  type MRT_ColumnDef,
  type TableFilterProps,
} from '@vrplatform/table';
import { type Maybe, toTitleCase } from '@vrplatform/utils';
import { Box, Text } from '@mantine/core';
import type {
  AutomationViewSettings,
  MappingLeftSchemaUnion,
} from '@vrplatform/ui-common';
import { type ComponentProps, useMemo } from 'react';
import { useAutomationsQuery } from '@vrplatform/data';
import { AutomationMappingEditor } from './AutomationMappingEditor';

type TableProps = ComponentProps<typeof InfiniteTable>;

type AutomationColumnParams = {
  targetKey: MappingLeftSchemaUnion;
  connectionId: string;
  filterByAutomationIds: Maybe<Maybe<string>[]>;
  filterByMappingKeys?: string[];
};

type RowData = { id: string };

export const AutomationMappingTable = <TData extends RowData>({
  automationColumnParams,
  columns: columnDefs,
  sorting,
  children,
  resetFilter,
  emptyRowsFallback,
  queryData,
}: {
  automationColumnParams: AutomationColumnParams;
  columns: MRT_ColumnDef<TData>[];
  queryData: TableProps['queryData'];
  sorting: TableProps['sorting'];
  emptyRowsFallback?: NonNullable<TableProps['table']>['emptyRowsFallback'];
} & TableFilterProps) => {
  const automationColumns = useAutomationMappingColumns(automationColumnParams);

  const columns = useMemo(() => {
    return [...columnDefs, ...automationColumns];
  }, [columnDefs, automationColumns]);

  return (
    <InfiniteTable
      columns={columns as any}
      table={{ emptyRowsFallback }}
      queryData={queryData}
      sorting={sorting}
      resetFilter={resetFilter || (() => {})}
    >
      {children}
    </InfiniteTable>
  );
};

function useAutomationMappingColumns<TData extends RowData>({
  connectionId,
  filterByAutomationIds,
  targetKey,
  filterByMappingKeys,
}: AutomationColumnParams) {
  const { data: automations = [] } = useAutomationsQuery();

  return useMemo(() => {
    return automations
      .filter((automation) => {
        const mappings = automation.mappings || {};

        const hasTargetKeyMapping = Object.values(mappings).some(
          (mapping) => mapping?.left?.schema === targetKey
        );

        const hasConnectionId = Object.values(automation.connections).includes(
          connectionId
        );

        const byFiltered =
          (filterByAutomationIds?.length || 0) < 1
            ? true
            : filterByAutomationIds?.includes(automation.automationId);

        return byFiltered && hasTargetKeyMapping && hasConnectionId;
      })
      .flatMap((automation) => {
        const isFilterByOneAutomation = filterByAutomationIds?.length === 1;

        const mappings = Object.entries(automation.mappings)
          .filter(([mappingKey, mapping]) => {
            const viewSettings = (
              automation.viewSettings as AutomationViewSettings
            )?.[mappingKey];

            const leftTypeOverwrite = viewSettings?.leftType;
            const left = leftTypeOverwrite || mapping?.left.schema;

            const includesMappingKeys =
              !filterByMappingKeys?.length ||
              filterByMappingKeys?.includes(mappingKey);

            const isLocal =
              typeof viewSettings?.isLocal === 'boolean'
                ? viewSettings?.isLocal
                : mapping.scope === 'local';

            return (
              includesMappingKeys &&
              left === targetKey &&
              (isFilterByOneAutomation ? true : !isLocal)
            );
          })
          .map(([mappingKey, mapping]) => ({
            mappingKey,
            ...mapping,
          }));

        // ** Column Definition
        return mappings.map<MRT_ColumnDef<TData>>((mapping) => {
          const viewSettings = (
            automation.viewSettings as AutomationViewSettings
          )?.[mapping.mappingKey];

          const isLocal =
            typeof viewSettings?.isLocal === 'boolean'
              ? viewSettings?.isLocal
              : mapping.scope === 'local';

          return {
            header: mapping?.label + (isLocal ? ' (Only this automation)' : ''),
            Header: () => (
              <Text component="p" m={0}>
                {mapping?.label}{' '}
                {isLocal && (
                  <>
                    <br />
                    <Text fw={400} display="inline" component="span">
                      (Only this automation)
                    </Text>
                  </>
                )}
              </Text>
            ),
            accessorKey: mapping?.mappingKey as any,
            enableSorting: false,
            minSize: 250,
            maxSize: 250,
            size: 250,
            Cell: ({ row }) => {
              const data = row.original;

              const leftType = mapping.left.schema;
              const rightType = mapping.right.schema;
              const settingKey = mapping.mappingKey;

              return (
                <Box maw={350} w="100%">
                  <AutomationMappingEditor
                    targetId={data.id}
                    automation={{
                      automationId: automation.automationId,
                      leftConnectionId: automation.leftConnectionId,
                      rightConnectionId: automation.rightConnectionId,
                      leftAppId: automation.leftConnection?.appId,
                      rightAppId: automation.rightConnection?.appId,
                      templateId: automation.templateId,
                    }}
                    mapping={{
                      leftType,
                      rightType,
                      settingKey,
                      isLocal: isLocal,
                      label: mapping.label || toTitleCase(settingKey)!,
                      altLeftTypes: mapping.left.alt,
                      exceptionFilters: mapping.params?.exceptionFilters,
                      rightParams: mapping.right?.params || {},
                      leftParams: mapping.left?.params || mapping.params || {},
                    }}
                    parentSetting={null}
                  />
                </Box>
              );
            },
          };
        });
      })
      .filter(
        (i, index, arr) =>
          arr.findIndex((j) => j.accessorKey === i.accessorKey) === index
      );
  }, [
    automations,
    connectionId,
    targetKey,
    filterByAutomationIds,
    filterByMappingKeys,
  ]);
}

export function useHasAutomationMappings(props: AutomationColumnParams) {
  return useAutomationMappingColumns(props).length > 0;
}
